export const WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY =
  "billiard-workforce-invitation-activation-v1";

const TOKEN_PATTERN = /^.{32,512}$/u;
const INVITATION_ID_PATTERN = /^invite_[a-f0-9]{32}$/;
const OTP_CHALLENGE_PATTERN = /^otp_[a-f0-9]{32}$/;
const WECHAT_CHALLENGE_PATTERN = /^wechat_[a-f0-9]{32}$/;

export interface WorkforceInvitationActivationRecovery {
  version: 1;
  activationToken: string;
  invitationId: string | null;
  identityIdempotencyKey: string;
  otpChallengeId: string | null;
  simulatedOtpCode: string | null;
  otpIdempotencyKey: string;
  wechatChallengeId: string | null;
  simulatedAuthorizationCode: string | null;
  wechatIdempotencyKey: string;
  activationIdempotencyKey: string;
  /** Whether this tab submitted ACTIVATE; never persist the backup password. */
  activationSubmitted: boolean;
}

function defaultKeyFactory(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function validIdempotencyKey(value: unknown): value is string {
  return typeof value === "string" && value.length >= 8 && value.length <= 128;
}

function nullablePattern(value: unknown, pattern: RegExp): value is string | null {
  return value === null || (typeof value === "string" && pattern.test(value));
}

function nullableBoundedString(value: unknown, min: number, max: number): value is string | null {
  return (
    value === null || (typeof value === "string" && value.length >= min && value.length <= max)
  );
}

export function createWorkforceInvitationActivationRecovery(
  activationToken: string,
  keyFactory: (prefix: string) => string = defaultKeyFactory,
): WorkforceInvitationActivationRecovery {
  if (!TOKEN_PATTERN.test(activationToken)) throw new Error("邀请链接中的安全凭证无效");
  return Object.freeze({
    version: 1,
    activationToken,
    invitationId: null,
    identityIdempotencyKey: keyFactory("workforce-invite-identity"),
    otpChallengeId: null,
    simulatedOtpCode: null,
    otpIdempotencyKey: keyFactory("workforce-invite-otp"),
    wechatChallengeId: null,
    simulatedAuthorizationCode: null,
    wechatIdempotencyKey: keyFactory("workforce-invite-wechat"),
    activationIdempotencyKey: keyFactory("workforce-invite-activate"),
    activationSubmitted: false,
  });
}

export function parseWorkforceInvitationActivationRecovery(
  raw: string | null,
): WorkforceInvitationActivationRecovery | null {
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const exactKeys = [
    "version",
    "activationToken",
    "invitationId",
    "identityIdempotencyKey",
    "otpChallengeId",
    "simulatedOtpCode",
    "otpIdempotencyKey",
    "wechatChallengeId",
    "simulatedAuthorizationCode",
    "wechatIdempotencyKey",
    "activationIdempotencyKey",
    "activationSubmitted",
  ].sort();
  const actualKeys = Object.keys(record).sort();
  const legacyKeys = exactKeys.filter((key) => key !== "activationSubmitted");
  if (
    !(
      (actualKeys.length === exactKeys.length &&
        actualKeys.every((key, index) => key === exactKeys[index])) ||
      (actualKeys.length === legacyKeys.length &&
        actualKeys.every((key, index) => key === legacyKeys[index]))
    ) ||
    record.version !== 1 ||
    typeof record.activationToken !== "string" ||
    !TOKEN_PATTERN.test(record.activationToken) ||
    !nullablePattern(record.invitationId, INVITATION_ID_PATTERN) ||
    !validIdempotencyKey(record.identityIdempotencyKey) ||
    !nullablePattern(record.otpChallengeId, OTP_CHALLENGE_PATTERN) ||
    !nullableBoundedString(record.simulatedOtpCode, 6, 6) ||
    (record.simulatedOtpCode !== null && !/^\d{6}$/.test(record.simulatedOtpCode)) ||
    !validIdempotencyKey(record.otpIdempotencyKey) ||
    !nullablePattern(record.wechatChallengeId, WECHAT_CHALLENGE_PATTERN) ||
    !nullableBoundedString(record.simulatedAuthorizationCode, 16, 256) ||
    !validIdempotencyKey(record.wechatIdempotencyKey) ||
    !validIdempotencyKey(record.activationIdempotencyKey) ||
    (record.activationSubmitted !== undefined && typeof record.activationSubmitted !== "boolean")
  ) {
    return null;
  }
  return Object.freeze({
    version: 1,
    activationToken: record.activationToken,
    invitationId: record.invitationId,
    identityIdempotencyKey: record.identityIdempotencyKey,
    otpChallengeId: record.otpChallengeId,
    simulatedOtpCode: record.simulatedOtpCode,
    otpIdempotencyKey: record.otpIdempotencyKey,
    wechatChallengeId: record.wechatChallengeId,
    simulatedAuthorizationCode: record.simulatedAuthorizationCode,
    wechatIdempotencyKey: record.wechatIdempotencyKey,
    activationIdempotencyKey: record.activationIdempotencyKey,
    activationSubmitted: record.activationSubmitted ?? false,
  });
}

export function readWorkforceInvitationTokenFromHash(hash: string): string | null {
  if (!hash.startsWith("#")) return null;
  const query = new URLSearchParams(hash.slice(1));
  if ([...query.keys()].length !== 1 || !query.has("token")) return null;
  const token = query.get("token");
  return token && TOKEN_PATTERN.test(token) ? token : null;
}

export function loadWorkforceInvitationActivationRecovery(
  storage: Storage,
): WorkforceInvitationActivationRecovery | null {
  const raw = storage.getItem(WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY);
  const parsed = parseWorkforceInvitationActivationRecovery(raw);
  if (raw !== null && parsed === null) {
    throw new Error("本标签页保存的邀请进度已经损坏，已停止继续提交");
  }
  return parsed;
}

export function saveWorkforceInvitationActivationRecovery(
  storage: Storage,
  state: WorkforceInvitationActivationRecovery,
): WorkforceInvitationActivationRecovery {
  const protectedState = parseWorkforceInvitationActivationRecovery(JSON.stringify(state));
  if (!protectedState) throw new Error("邀请进度内容无法安全保存");
  const raw = JSON.stringify(protectedState);
  storage.setItem(WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY, raw);
  const confirmed = parseWorkforceInvitationActivationRecovery(
    storage.getItem(WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY),
  );
  if (!confirmed || JSON.stringify(confirmed) !== raw) {
    throw new Error("邀请进度无法安全保存，已停止继续提交");
  }
  return confirmed;
}

export function ensureWorkforceInvitationActivationRecovery(
  storage: Storage,
  expected: WorkforceInvitationActivationRecovery,
): WorkforceInvitationActivationRecovery {
  const current = loadWorkforceInvitationActivationRecovery(storage);
  if (!current || JSON.stringify(current) !== JSON.stringify(expected)) {
    throw new Error("当前标签页保存的邀请进度已经变化，已停止继续提交");
  }
  return current;
}

export function clearWorkforceInvitationActivationRecovery(storage: Storage): void {
  storage.removeItem(WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY);
  if (storage.getItem(WORKFORCE_INVITATION_ACTIVATION_STORAGE_KEY) !== null) {
    throw new Error("邀请安全凭证无法安全清除，请关闭当前标签页");
  }
}
